import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Optional,
  Self,
  output,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import moment from 'moment-timezone';

import { TimeService } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { DATE_FORMAT, getDefaultTimezone } from '@core/time-legacy';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  GroupComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
  RtInput,
  SegmentedButtonType,
  SegmentedControlComponent,
} from '@core/user-interface-legacy';

import { TimePickerDropdownComponent } from './time-picker-dropdown/time-picker-dropdown.component';
import { DatePickerComponent, DatePickerDirection } from '../date-picker/date-picker.component';
import { TimeZonePickerComponent } from '../time-zone-picker/time-zone-picker.component';

export type TimeData = {
  date: string; // 'YYYY-MM-DD'
  time: string; // 'HH:mm'
  zone: string; // 'Europe/Warsaw'
};

@Component({
  selector: 'rt-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(focus)': 'focus.emit($event)',
    '(blur)': 'blur.emit($event)',
  },
  imports: [
    ButtonComponent,
    ConnectedOverlayDirective,
    DatePickerComponent,
    GroupComponent,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    SegmentedControlComponent,
    TimePickerDropdownComponent,
    TimeZonePickerComponent,
    TranslatePipe,
  ],
})
export class TimePickerComponent extends RtInput<TimeData | null> implements OnChanges {
  @Input() public defaultDate: string = moment().format(DATE_FORMAT);
  @Input() public defaultTime: string = '12:00';
  // f.e. "Europe/Warsaw"
  @Input() public defaultTimeZoneName: string = getDefaultTimezone();
  @Input() public showDoneButton: boolean = true;
  @Input() public blockPastTime: boolean = false;
  @Input() public direction: DatePickerDirection = 'any';

  @Input() public timeInterval: number = 0;

  public isTimeValid = output<boolean>();

  public activeTab: 'date' | 'time' = 'date';
  public showTimeZonePicker: boolean = false;
  public isPresentDaySelected: boolean = false;
  public tmpValue: TimeData | null;

  public segmentedButtons: Array<SegmentedButtonType<'date' | 'time'>>;

  constructor(
    private i18n: I18nService,
    private timeService: TimeService,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
    this.setTmpValue(this.value, false);
    this.setSegmentedButtons();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.setTmpValue({
      date: this.defaultDate,
      time: this.defaultTime,
      zone: this.defaultTimeZoneName,
    });

    if (changes.defaultDate || changes.defaultTime) {
      this.setIsPresentDaySelected(this.defaultDate);
      this.validatePastTime(this.defaultTime);
    }
  }

  public handleDateChange(date: string | string[]): void {
    if (date) {
      this.tmpValue = <TimeData>{
        ...this.tmpValue,
        date,
      };
      this.setIsPresentDaySelected(date);
      this.validatePastTime(this.tmpValue.time);
      this.cdRef.detectChanges();
    }
  }

  public handleTimeChange(time: string): void {
    this.validatePastTime(time);
    this.tmpValue = <TimeData>{
      ...this.tmpValue,
      time,
    };
    this.cdRef.detectChanges();
  }

  public handleTimeZoneChange(zone: string): void {
    this.tmpValue = <TimeData>{
      ...this.tmpValue,
      zone,
    };
    this.validatePastTime(this.tmpValue.time);
    this.cdRef.detectChanges();
  }

  public applyChanges(): void {
    this.handleChange(this.tmpValue);
    this.cdRef.detectChanges();
  }

  public switchTab(tab: 'date' | 'time'): void {
    this.activeTab = tab;
    this.setSegmentedButtons();
    this.cdRef.detectChanges();
  }

  protected setValue(value: TimeData | null): void {
    this.value = value;
    this.setTmpValue(value);
  }

  private validatePastTime(time: string): void {
    if (this.blockPastTime) {
      const isTimeValid =
        !this.isPresentDaySelected ||
        (this.timeService.isDateValid(time, 'LT') && !this.timeService.isBeforeNow(time, 'LT'));

      this.isTimeValid.emit(isTimeValid);
    }
  }

  private setIsPresentDaySelected(date: string | string[]): void {
    if (!Array.isArray(date) && this.blockPastTime) {
      this.isPresentDaySelected = this.timeService.isToday(date);
    }
  }

  private setTmpValue(value: TimeData | null, runDetectChanges: boolean = false): void {
    const time = (value && value.time) || this.defaultTime;
    this.tmpValue = {
      date: (value && value.date) || this.defaultDate,
      time: moment(time, 'HH:mm').format('LT'),
      zone: (value && value.zone) || this.defaultTimeZoneName,
    };
    if (runDetectChanges) {
      this.cdRef.detectChanges();
    }
  }

  private setSegmentedButtons(): void {
    this.segmentedButtons = [
      {
        isActive: this.activeTab === 'date',
        value: 'date',
        label: this.i18n.translate('rtu.common.date'),
        isAvailable: true,
      },
      {
        isActive: this.activeTab === 'time',
        value: 'time',
        label: this.i18n.translate('rtu.common.time'),
        isAvailable: true,
      },
    ];
  }
}
